import React, { useState, useEffect } from 'react';

import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Formik, Form as FormikForm, Field, useFormikContext, ErrorMessage } from 'formik';
import { DateTime } from 'luxon';
import { orderBy } from 'lodash';

import { useAppSelector } from 'hooks';
import { Select } from 'components/Forms';
import {
  getAdminsByGid,
  getGroupTZ,
  getInterventions,
  selectCurrentGid
} from 'store/groups/reducer';
import { TaskType, PatientStatus, ComparisonOperator, TaskTriggerEvent } from 'constants/enums';
import { Error, Loading } from 'components/Fetch';
import { useAxios } from 'configs/requests';
import { formatDropdownOptions } from 'utils/Object';
import { OverdueUnit } from 'utils/tasks';
import { isUnscheduledRecurrence } from 'utils/RecurrenceEngine';
import { InterventionType } from 'utils/Task';
import { DateTimePickerField } from 'components/UI';
import { formatTimeStringForRecurrence } from 'utils/Date';
import ComplexSwitchFormik from 'components/Forms/ComplexSwitchFormik';
import { getPatientStatusOptions } from 'utils/UIStoreFunctions';

import RecurrenceEngine from '../../RecurrenceEngine';

interface IAddClinicalTaskModalProps {
  handleClose: (refresh?: boolean) => void;
  task?: ITask | null;
  carePlanTemplate?: ICarePlanTemplate;
}

interface IAddClinicalTaskForm {
  interventionId?: string;
  startDate?: Date;
  endDate?: Date;
  startTime?: string;
  endTime?: string;
  isOverdue: boolean;
  overdueValue?: number;
  overdueUnit?: string;
  assignees: ISelectOption[];
  startAfter?: number;
  endAfter?: number;
  triggerType?: TaskTriggerEvent;
  triggerStatus?: PatientStatus;
}

const AddEditClinicalTaskModal = ({
  handleClose,
  task,
  carePlanTemplate
}: IAddClinicalTaskModalProps) => {
  const intl = useIntl();
  const uid = useAppSelector(({ patient }) => patient.data?.uid);
  const patientGid = useAppSelector(({ patient }) => patient.data?.subgroup_gid);
  const selectedGid = useAppSelector(selectCurrentGid);
  const gid = patientGid || selectedGid;
  const orgGid = useAppSelector(({ patient }) => patient.data?.gid) || selectedGid;
  const locale = useAppSelector(({ intl }) => intl.locale);
  const interventions = useAppSelector(getInterventions);
  const groupTimezone = useAppSelector(getGroupTZ);
  const admins = useAppSelector((state) => getAdminsByGid(state, gid || ''));
  const assignedOptions = formatDropdownOptions(admins, 'aid', 'name');

  const [recurrence, setRecurrence] = useState(task?.recurrence.rrule || '');
  const [validRecurrence, setValidRecurrence] = useState(task ? true : false);
  const [frequency, setFrequency] = useState<string | null>(task?.recurrence.rruleNameKey || null);

  const getPostUrl = (values: IAddClinicalTaskForm) => {
    if (carePlanTemplate) {
      return '/clinician/2.0.0/CarePlanTemplate';
    } else if (values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE) {
      return '/clinician-gateway/careplan-triggers';
    } else {
      return '/clinician-gateway/Tasks';
    }
  };

  const getPatchUrl = (values: IAddClinicalTaskForm) => {
    if (values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE) {
      return `/clinician-gateway/careplan-triggers/${task?.triggerId}`;
    } else {
      return '/clinician-gateway/Tasks';
    }
  };

  const [{ loading: loadingPostTask, error: errorPostTask }, post] = useAxios(
    {
      method: 'POST'
    },
    { manual: true }
  );

  const [{ loading: loadingPatchTask, error: errorPatchTask }, patch] = useAxios(
    {
      method: 'PATCH'
    },
    { manual: true }
  );

  const overdueUnits: ISelectOption[] = [
    { value: OverdueUnit.MINUTE, label: intl.formatMessage({ id: 'minutesKey' }) },
    { value: OverdueUnit.HOUR, label: intl.formatMessage({ id: 'hoursKey' }) },
    { value: OverdueUnit.DAY, label: intl.formatMessage({ id: 'daysKey' }) },
    { value: OverdueUnit.WEEK, label: intl.formatMessage({ id: 'weeksKey' }) },
    { value: OverdueUnit.MONTH, label: intl.formatMessage({ id: 'monthsKey' }) }
  ];

  const extractTimeFromEpoch = (epoch: number) => {
    const dateTime = DateTime.fromMillis(epoch, { zone: groupTimezone }).setLocale(locale);
    return `${dateTime.hour.toString().padStart(2, '0')}:${dateTime.minute
      .toString()
      .padStart(2, '0')}`;
  };

  const extractCompleteByFromDuration = () => {
    if (task?.completeBy) {
      let unit = OverdueUnit.MINUTE;
      if (task.completeBy.hours) {
        unit = OverdueUnit.HOUR;
      } else if (task.completeBy.days) {
        unit = OverdueUnit.DAY;
      } else if (task.completeBy.weeks) {
        unit = OverdueUnit.WEEK;
      } else if (task.completeBy.months) {
        unit = OverdueUnit.MONTH;
      }
      return {
        overdueUnit: unit,
        overdueValue: task.completeBy[unit as keyof Duration]
      };
    }
    return {};
  };

  const formatCompleteByToDuration = (values: IAddClinicalTaskForm) => {
    if (values.overdueValue) {
      switch (values.overdueUnit) {
        case OverdueUnit.MINUTE:
          return { minutes: values.overdueValue };
        case OverdueUnit.HOUR:
          return { hours: values.overdueValue };
        case OverdueUnit.DAY:
          return { days: values.overdueValue };
        case OverdueUnit.WEEK:
          return { weeks: values.overdueValue };
        case OverdueUnit.MONTH:
          return { months: values.overdueValue };
      }
    }
    return undefined;
  };

  const formatAssignees = (assignees: ISelectOption[]) => {
    return assignees.map((assignee) => assignee.value);
  };

  const formatRecurrence = (startDate?: DateTime, endDate?: DateTime) => {
    if (!recurrence) return undefined;
    const dateFormat = 'yyyyMMdd';
    const timeFormat = 'HHmmss';
    const dtStart = startDate
      ? `DTSTART;TZID=${groupTimezone}:${startDate.toFormat(dateFormat)}T${startDate.toFormat(
          timeFormat
        )}\n`
      : '';
    const until = endDate
      ? `;UNTIL=${endDate.toFormat(dateFormat)}T${endDate.toFormat(timeFormat)}`
      : '';
    return `${dtStart}${recurrence}${until}`;
  };

  const createDateTimeFromInputs = (time?: string, date?: Date) => {
    if (!date || !time) return undefined;
    const splitTime = time.split(':');
    const tzAdjustedDate = DateTime.fromJSDate(date)
      .set({
        hour: parseInt(splitTime[0]),
        minute: parseInt(splitTime[1])
      })
      .setZone(groupTimezone);
    return tzAdjustedDate;
  };

  const filterAssignees = (assignees: string[]) => {
    return assignedOptions.filter((assignee) => assignees.includes(assignee.value));
  };

  const defaultValues: IAddClinicalTaskForm = task
    ? {
        interventionId: task.interventionId,
        startDate: task.startDate
          ? DateTime.fromMillis(task.startDate, { zone: groupTimezone })
              .setLocale(locale)
              .toJSDate()
          : undefined,
        endDate: task.endDate
          ? DateTime.fromMillis(task.endDate, { zone: groupTimezone }).setLocale(locale).toJSDate()
          : undefined,
        startTime: task.startDate ? extractTimeFromEpoch(task.startDate) : task.startTime || '',
        endTime: task.endDate ? extractTimeFromEpoch(task.endDate) : task.endTime || '',
        isOverdue: !!task.completeBy,
        assignees: filterAssignees(task.assignees),
        ...extractCompleteByFromDuration(),
        startAfter: task.startAfter?.days,
        endAfter: task.endAfter?.days,
        triggerType: task.trigger
          ? (task.trigger.event as TaskTriggerEvent)
          : TaskTriggerEvent.ON_APPLY,
        triggerStatus: task.trigger?.details.value.toString() as PatientStatus | undefined
      }
    : {
        isOverdue: false,
        assignees: [],
        startTime: '00:00',
        endTime: '00:00',
        triggerType: TaskTriggerEvent.ON_APPLY,
        triggerStatus: undefined
      };

  const userCreatedInterventions = (interventions.userCreated as any)[orgGid || ''] || [];
  const userCreatedOptions =
    userCreatedInterventions.length > 0
      ? [
          {
            label: intl.formatMessage({ id: 'organizationSpecificKey' }),
            options: userCreatedInterventions.map((intervention: any) => ({
              label: intervention.name,
              value: intervention.id
            }))
          }
        ]
      : [];

  const managedInterventionOptions = Object.entries(
    interventions.managed
      .filter((intervention) => intervention.type === InterventionType.CLINICAL)
      .reduce((categories: any, intervention) => {
        const categoryOptions = categories[intervention.category.name] || [];
        return {
          ...categories,
          [intervention.category.name]: [
            ...categoryOptions,
            {
              label: intl.formatMessage({ id: intervention.name }),
              value: intervention.id
            }
          ]
        };
      }, {})
  ).map(([category, interventions]) => {
    return {
      label: intl.formatMessage({ id: category }),
      options: interventions
    };
  });

  const interventionOptions = [...userCreatedOptions, ...managedInterventionOptions];

  const validation = (values: IAddClinicalTaskForm) => {
    const errors: any = {};
    if (!values.interventionId) {
      errors.interventionId = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }
    if (!validRecurrence) {
      errors.frequency = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    }
    if (!carePlanTemplate && values.triggerType === TaskTriggerEvent.ON_APPLY) {
      if (!values.startDate) {
        errors.startDate = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
      if (!values.startTime) {
        errors.startTime = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
    } else {
      if (!values.startAfter && values.startAfter !== 0) {
        errors.startAfter = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
    }

    // validates end date is after start date
    if (!carePlanTemplate && values.triggerType === TaskTriggerEvent.ON_APPLY) {
      const startDateTime = createDateTimeFromInputs(values.startTime, values.startDate);
      const endDateTime = createDateTimeFromInputs(values.endTime, values.endDate);
      if (startDateTime) {
        if (endDateTime && endDateTime <= startDateTime) {
          errors.endDate = intl.formatMessage({ id: 'endDateBeforeStartDateKey' });
        }
        if (!task && startDateTime <= DateTime.now()) {
          errors.startDate = intl.formatMessage({ id: 'taskStartDateBeforeTodayKey' });
        }
      }
    } else if (
      (values.endAfter || values.endAfter === 0) &&
      values.endAfter <= (values.startAfter || 0)
    ) {
      errors.endAfter = intl.formatMessage({ id: 'endAfterBeforeStartAfterKey' });
    }

    // validates overdue only if it's checked
    if (values.isOverdue) {
      if (!values.overdueValue) {
        errors.overdueValue = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
      if (!values.overdueUnit) {
        errors.overdueUnit = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
    }

    if (values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE) {
      if (!values.triggerStatus) {
        errors.triggerStatus = intl.formatMessage({ id: 'FieldNotEmptyKey' });
      }
    }

    return errors;
  };

  const createClinicalTaskPayload = (values: IAddClinicalTaskForm) => {
    const startDateTime = createDateTimeFromInputs(values.startTime, values.startDate);
    const endDateTime = createDateTimeFromInputs(values.endTime, values.endDate);
    return {
      interventionId: values.interventionId || '',
      uid: uid || '',
      recurrence: {
        rruleNameKey: frequency || '',
        rrule: formatRecurrence(startDateTime, endDateTime)
      },
      type: TaskType.CLINICAL,
      startDate: startDateTime?.toMillis() || 0,
      endDate: endDateTime?.toMillis(),
      completeBy: formatCompleteByToDuration(values),
      assignees: formatAssignees(values.assignees),
      ids: [task?.id || -1]
    };
  };

  const createTriggerPayload = (values: IAddClinicalTaskForm) => {
    return {
      uid,
      event: TaskTriggerEvent.ON_STATUS_CHANGE,
      details: {
        value: parseInt(values.triggerStatus || '0'),
        comparisonOperator: 'eq',
        startAfter: { days: values.startAfter },
        endAfter: values.endAfter ? { days: values.endAfter } : undefined
      },
      itemType: 'task',
      itemContent: JSON.stringify(createCareplanTemplatePayload(values))
    };
  };

  const createCareplanTemplatePayload = (values: IAddClinicalTaskForm) => {
    const startDateTime = createDateTimeFromInputs(values.startTime, values.startDate);
    const endDateTime = createDateTimeFromInputs(values.endTime, values.endDate);
    const isOnApply = values.triggerType === TaskTriggerEvent.ON_APPLY;

    const startAfterDuration = { days: values.startAfter || 0 };
    const endAfterDuration = values.endAfter ? { days: values.endAfter } : undefined;

    return {
      interventionId: values.interventionId || '',
      recurrence: {
        rruleNameKey: frequency || '',
        rrule: formatRecurrence(startDateTime, endDateTime)
      },
      type: TaskType.CLINICAL,
      completeBy: formatCompleteByToDuration(values),
      assignees: formatAssignees(values.assignees),
      edit_status: task ? 'updated' : 'new',
      id: task?.id,
      startAfter: startAfterDuration,
      endAfter: endAfterDuration,
      startTime: values.startTime,
      endTime: values.endTime,
      trigger: !isOnApply
        ? {
            event: TaskTriggerEvent.ON_STATUS_CHANGE,
            details: {
              value: values.triggerStatus ? (values.triggerStatus as PatientStatus) : '',
              comparisonOperator: ComparisonOperator.EQ,
              startAfter: startAfterDuration,
              endAfter: endAfterDuration
            }
          }
        : undefined
    };
  };

  const handleSubmit = async (values: IAddClinicalTaskForm) => {
    if (carePlanTemplate) {
      await post({
        url: getPostUrl(values),
        data: {
          carePlanTemplate: carePlanTemplate,
          tasks: [createCareplanTemplatePayload(values)]
        }
      });
    } else {
      const data =
        values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE
          ? createTriggerPayload(values)
          : createClinicalTaskPayload(values);
      if (task) {
        await patch({ url: getPatchUrl(values), data });
      } else {
        await post({ url: getPostUrl(values), data });
      }
    }
    handleClose(true);
  };

  // reset state if modal is closed
  useEffect(() => {
    if (task) {
      if (task.recurrence.rrule) {
        setRecurrence(task.recurrence.rrule);
      }
      setValidRecurrence(true);
      setFrequency(task.recurrence.rruleNameKey);
    }
  }, [task]);

  const AddClinicalTaskForm = () => {
    const { values, validateForm, isValid } = useFormikContext<IAddClinicalTaskForm>();
    const useRelativeDateSelectors =
      carePlanTemplate || values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE;

    const updateRecurrence = (recurrence: string, valid: boolean, frequency: string | null) => {
      setRecurrence(recurrence);
      setValidRecurrence(valid);
      setFrequency(frequency);
    };

    useEffect(() => {
      if (!recurrence && task?.recurrence) {
        updateRecurrence(task.recurrence.rrule || '', true, task.recurrence.rruleNameKey);
      }
    }, []);

    useEffect(() => {
      validateForm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validRecurrence, validateForm]);

    return (
      <FormikForm autoComplete="off">
        <Modal.Header className="align-items-center">
          <Modal.Title>
            {intl.formatMessage({
              id: `patient.careplan.tasks.${task ? 'edit' : 'add'}.modalTitle`
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(errorPostTask || errorPatchTask) && <Error retry={() => handleSubmit(values)} />}
          <Row>
            <Form.Group className="form-group" as={Col}>
              <Select
                name="interventionId"
                dataTestId="careplan-clinical-tasks-intervention"
                label={intl.formatMessage({ id: 'interventionKey' })}
                options={interventionOptions}
                required
                groupedOptions
                isDisabled={task && !carePlanTemplate}
                wrapLines={true}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="form-group" as={Col}>
              <Select
                name="assignees"
                label={intl.formatMessage({ id: 'assignedTo' })}
                dataTestId="careplan-clinical-tasks-assignees"
                placeholder={intl.formatMessage({ id: 'selectUserKey' })}
                options={orderBy(
                  assignedOptions,
                  [(option) => option.label.toLowerCase(), 'name'],
                  'asc'
                )}
                isClearable={false}
                isMulti
                isSearchable
                useAvatarNameColor
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="form-group" as={Col}>
              <RecurrenceEngine
                recurrence={recurrence}
                frequency={frequency}
                onChange={(recurrence: string, valid: boolean, frequency: string | null) =>
                  updateRecurrence(recurrence, valid, frequency)
                }
                isActive={true}
                date={DateTime.now()
                  .setLocale(locale)
                  .set(formatTimeStringForRecurrence(values.startTime))}
                legacyOptions={false}
              />
            </Form.Group>
          </Row>
          <Form.Group className="form-group" as={Col}>
            <ComplexSwitchFormik
              label={intl.formatMessage({ id: 'tasks.trigger.triggerType' })}
              name="triggerType"
              className="w-100 mb-2"
              options={[
                {
                  label: intl.formatMessage({ id: 'tasks.trigger.triggerTypes.onApply' }),
                  value: TaskTriggerEvent.ON_APPLY
                },
                {
                  label: intl.formatMessage({
                    id: 'tasks.trigger.triggerTypes.onStatusChange'
                  }),
                  value: TaskTriggerEvent.ON_STATUS_CHANGE
                }
              ]}
            />
            {values.triggerType === TaskTriggerEvent.ON_STATUS_CHANGE && (
              <Select
                name="triggerStatus"
                dataTestId="careplan-clinical-tasks-trigger-status"
                label={intl.formatMessage({ id: 'tasks.trigger.triggerStatus' })}
                options={getPatientStatusOptions()}
                isClearable={false}
                required={!!values.triggerType}
              />
            )}
          </Form.Group>
          <Row>
            <Form.Group className="form-group" as={Col}>
              {!useRelativeDateSelectors ? (
                <DateTimePickerField
                  prefix="start"
                  minDate={DateTime.now()}
                  dateLabelKey="startDateKey"
                  required={true}
                  disabled={!!task}
                />
              ) : (
                <>
                  <Form.Label>{`${intl.formatMessage({ id: 'startAfterKey' })} *`}</Form.Label>
                  <div className="d-flex align-items-center">
                    <Field
                      data-test="task-template-start-after"
                      size="sm"
                      type="number"
                      className="form-control w-40 me-2"
                      name="startAfter"
                      id="startAfter"
                      required={true}
                      min={0}
                    />
                    <Form.Label>{intl.formatMessage({ id: 'daysKey' })}</Form.Label>
                    <Form.Label className="ms-5">{intl.formatMessage({ id: 'atKey' })}</Form.Label>
                    <Field
                      className="form-control w-30 ms-2"
                      data-test="task-template-start-after-time"
                      size="sm"
                      type="time"
                      name="startTime"
                      id="startTime"
                    />
                  </div>
                  <Form.Control.Feedback className="d-block" type="invalid">
                    <ErrorMessage name="startAfter" />
                  </Form.Control.Feedback>
                </>
              )}
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="form-group" as={Col}>
              {!useRelativeDateSelectors ? (
                <DateTimePickerField
                  prefix="end"
                  minDate={DateTime.now()}
                  dateLabelKey="endDateKey"
                />
              ) : (
                <>
                  <Form.Label>{intl.formatMessage({ id: 'endAfterKey' })}</Form.Label>
                  <div className="d-flex align-items-center">
                    <Field
                      data-test="task-template-end-after"
                      size="sm"
                      type="number"
                      className="form-control w-40 me-2"
                      name="endAfter"
                      id="endAfter"
                      min={1}
                    />
                    <Form.Label className="m-0">{intl.formatMessage({ id: 'daysKey' })}</Form.Label>
                    <Form.Label className="ms-5">{intl.formatMessage({ id: 'atKey' })}</Form.Label>
                    <Field
                      className="form-control w-30 ms-2"
                      data-test="task-template-end-after-time"
                      size="sm"
                      type="time"
                      name="endTime"
                    />
                  </div>

                  <Form.Control.Feedback className="d-block" type="invalid">
                    <ErrorMessage name="endAfter" />
                  </Form.Control.Feedback>
                </>
              )}
            </Form.Group>
          </Row>
          {!isUnscheduledRecurrence(frequency) && (
            <>
              <Row>
                <Form.Group className="form-group" as={Col}>
                  <Field
                    type="checkbox"
                    name="isOverdue"
                    id="isOverdue"
                    label={intl.formatMessage({ id: 'taskTimeSensitiveKey' })}
                    as={Form.Check}
                    data-test="careplan-clinical-tasks-isOverdue"
                  />
                </Form.Group>
              </Row>
              {values.isOverdue && (
                <Row>
                  <Form.Group className="form-group" as={Col} md={2}>
                    <Field
                      type="number"
                      name="overdueValue"
                      className="form-control me-2"
                      id="overdueValue"
                      min={1}
                      data-test="careplan-clinical-tasks-overdueValue"
                    />
                  </Form.Group>
                  <Form.Group className="form-group" as={Col} md={4}>
                    <Select
                      name="overdueUnit"
                      dataTestId="careplan-clinical-tasks-overdueUnit"
                      variant="form"
                      options={overdueUnits}
                      containerNoWrap
                    />
                  </Form.Group>
                </Row>
              )}
            </>
          )}
          {(loadingPostTask || loadingPatchTask) && <Loading className="mt-4" />}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={() => handleClose()}
            data-test="careplan-clinical-tasks-modal-close-btn"
          >
            {intl.formatMessage({ id: 'closeKey' })}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!isValid}
            data-test="careplan-clinial-tasks-modal-submit-btn"
          >
            {intl.formatMessage({ id: task ? 'save' : 'add' })}
          </Button>
        </Modal.Footer>
      </FormikForm>
    );
  };

  return (
    <Modal show onHide={handleClose} backdrop="static" data-test="careplan-clinical-tasks-modal">
      <Formik initialValues={defaultValues} validate={validation} onSubmit={handleSubmit}>
        <AddClinicalTaskForm />
      </Formik>
    </Modal>
  );
};

export default AddEditClinicalTaskModal;
